import { jwtDecode } from "jwt-decode";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import medicGeekApi from "../../config/axiosConfig";
import {
  showConfirmationAlert,
  showErrorAlert,
  showSuccessAlert,
} from "./../../components/alerts";

const ProfileForm = ({ onCloseForm, profileToEdit }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    logo1: null,
    logo2: null,
    passwordActual: "",
    password: "",
    confirmPassword: "",
  });
  const { profileId } = useParams();
  const [profileData, setProfileData] = useState(null);
  const history = useNavigate();
  const [paises, setPaises] = useState([]);
  const [provincias, setProvincias] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [countries, setCountries] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalFormData, setModalFormData] = useState({
    passwordActual: "",
    password: "",
    confirmPassword: "",
  });
  const [username, setUsername] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Obtener el token desde localStorage
        const token = localStorage.getItem("token");
        if (token) {
          // Decodificar el token para obtener el nombre de usuario
          const decodedToken = jwtDecode(token);
          const username = decodedToken.username; // Asegúrate de que 'username' sea correcto

          if (username) {
            // Buscar perfil por nombre de usuario
            const response = await medicGeekApi.get(
              `/profile/user/${username}`
            );
            const data = response.data;

            // Verificar si se recibió el perfil
            if (data) {
              setProfileData(data);
              setFormData(data);
              setIsEditing(true);
            } else {
              console.error("No se encontró el perfil del usuario.");
            }
          } else {
            console.error("No se pudo obtener el nombre de usuario del token.");
          }
        } else {
          console.error("No se encontró el token.");
        }
      } catch (error) {
        console.error("Error fetching data:", error.message);
      }
    };
    loadProvincias();
    loadMunicipios();
    loadCountries();
    fetchData();
  }, [profileId]);

  const loadProvincias = async () => {
    try {
      const provinciasResponse = await medicGeekApi.get("/provincias");
      const provinciasData = provinciasResponse.data;
      setProvincias(provinciasData);
    } catch (error) {
      console.error("Error al obtener los datos de la provincias", error);
    }
  };

  const loadMunicipios = async () => {
    try {
      const municipiosResponse = await medicGeekApi.get("/municipios");
      const municipiosData = municipiosResponse.data;
      setMunicipios(municipiosData);
    } catch (error) {
      console.error("Error al obtener los datos de municipios", error);
    }
  };

  const loadCountries = async () => {
    try {
      const countriesResponse = await medicGeekApi.get("/countries");
      const countriesData = countriesResponse.data;
      setCountries(countriesData);
    } catch (error) {
      console.error("Error al obtener los datos de countries", error);
    }
  };

  const getDefaultFormData = () => ({
    user: "",
    docNom: "Dr. Juan Pérez",
    nomCentro: "Centro de Salud Médico Juan Pérez",
    docEsp: "Cardiologo",
    docDir: "C/ Juan Pablo Duarte, Santo Domingo, RD.",
    docExe: "1234",
    docEmail: "correo@correo.com",
    docTel: "809-000-0000",
    docExt: "2255",
    docCel: "829-000-0000",
  });

  const handleInputChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const handleFileInputChange = (field, file) => {
    if (file instanceof Blob || file instanceof File) {
      const reader = new FileReader();
      reader.onload = () => {
        setFormData({
          ...formData,
          [field]: reader.result,
        });
      };
      reader.readAsDataURL(file);
    } else {
      console.error("El archivo seleccionado no es válido.");
    }
  };

  const guardarProfile = async () => {
    try {
      // Realiza la consulta al endpoint para obtener el perfil según username o docEmail
      const response = await medicGeekApi.post(`/profile/${formData.user}`);

      if (response.ok) {
        const perfil = await response.json();

        // Verifica si se encontró un perfil válido
        if (
          perfil.username === formData.user ||
          perfil.docEmail === formData.user
        ) {
          showSuccessAlert("El perfil existe");
        } else {
          showErrorAlert("No se encontró ningún perfil correspondiente");
        }
      } else {
        showErrorAlert(
          "No se pudo consultar el perfil. Por favor, inténtelo de nuevo."
        );
      }
    } catch (error) {
      console.error("Error al consultar el perfil:", error);
      showErrorAlert(
        "Hubo un error al consultar el perfil. Por favor, inténtelo de nuevo más tarde."
      );
    }
  };

  const editarProfile = async () => {
    try {
      const originalData = profileData;
      const updateData = {};

      const excludedProperties = ["showErrorAlert", "otraPropiedad"]; // Agrega aquí cualquier propiedad que deba excluirse

      for (const field in formData) {
        if (
          !excludedProperties.includes(field) &&
          formData[field] !== originalData[field]
        ) {
          updateData[field] = formData[field];
        }
      }

      if (Object.keys(updateData).length === 0) {
        onCloseForm();
        return;
      }

      const confirmResult = await showConfirmationAlert(
        "Confirmación",
        "¿Está seguro que desea actualizar el Perfil?"
      );

      if (confirmResult.isConfirmed) {
        // Actualizar el perfil usando medicGeekApi.patch
        const response = await medicGeekApi.patch(
          `/profile/${originalData.profileId}`,
          updateData
        );

        if (response.status === 200) {
          // El perfil se actualizó correctamente
          showSuccessAlert("El registro fue Actualizado Correctamente");
        } else {
          // Mostrar un mensaje de error específico para una respuesta de estado inesperada
          showErrorAlert(
            "Hubo un error inesperado al actualizar el Perfil. Por favor, inténtelo de nuevo."
          );
        }
      }
    } catch (error) {
      // Mostrar un mensaje de error genérico para cualquier otro error
      console.error("Error al editar el Perfil:", error);
      showErrorAlert(
        "Hubo un error al editar el Perfil. Por favor, inténtelo de nuevo."
      );
    }
  };

  const handleSubmit = async () => {
    // Lógica de manejo de envío de formulario
    if (isEditing) {
      await editarProfile();
    } else {
      await guardarProfile();
    }
  };

  const handleCancel = () => {
    // Puedes agregar una confirmación si hay cambios no guardados
    onCloseForm();
  };

  // Modal para restaurar contraseña
  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const handleModalInputChange = (field, value) => {
    setFormData((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleChange = (e) => {
    setModalFormData({
      ...modalFormData,
      [e.target.name]: e.target.value,
    });
  };

  const handleResetPassword = async () => {
    try {
      // Verificar y obtener los valores de formData
      const { passwordActual, password, confirmPassword } = formData;

      // Validación básica de las contraseñas
      if (password !== confirmPassword) {
        console.log("Las contraseñas no coinciden");
        showErrorAlert("Las contraseñas no coinciden");
        return;
      }

      // Verificar si los valores de las contraseñas están definidos
      if (!passwordActual || !password) {
        console.log(
          "La contraseña actual o la nueva contraseña no están definidas"
        );
        showErrorAlert(
          "La contraseña actual o la nueva contraseña no están definidas"
        );
        return;
      }

      // Obtener el token desde localStorage
      const token = localStorage.getItem("token");
      if (!token) {
        console.log("Token no está disponible");
        showErrorAlert("Token no está disponible");
        return;
      }

      // Decodificar el token para obtener el username
      const decodedToken = jwtDecode(token);
      const username = decodedToken?.username;
      if (!username) {
        console.log("Username no está definido en el token");
        showErrorAlert("Username no está definido en el token");
        return;
      }

      // // Verifica que se está enviando el objeto correcto
      // console.log("Datos que se envían en la solicitud:", {
      //   currentPassword: passwordActual,
      //   newPassword: password,
      // });

      // Llamada a la API para cambiar la contraseña
      const response = await medicGeekApi.patch(
        `/users/${username}/password`,
        {
          currentPassword: formData.passwordActual,
          newPassword: formData.password,
          confirmPassword: formData.confirmPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      showSuccessAlert("Contraseña cambiada correctamente");
      toggleModal(); // Cerrar el modal después de enviar los datos
    } catch (error) {
      console.error("Error al cambiar la contraseña:", error);
      console.error("Datos de error:", error.response?.data);
      showErrorAlert("Hubo un error al cambiar la contraseña");
    }
  };

  return (

  <div className="pro-container">

    {/* ================= HEADER ================= */}
    <div className="pro-header">

      <div className="pro-header-left">

        <h2 className="pro-title">
          Información del Usuario
        </h2>

        <p className="pro-subtitle">
          Gestión de datos personales y configuración del perfil
        </p>

      </div>

      <div className="pro-actions">

        <button
          className="pro-btn-cancel"
          onClick={toggleModal}
        >
          <i className="bi bi-shield-lock me-2"></i>
          Restaurar Contraseña
        </button>

      </div>

    </div>

    {/* ================= MODAL ================= */}
    <Modal
      isOpen={isModalOpen}
      toggle={toggleModal}
      centered
      modalClassName="pro-modal-md"
    >

      <ModalHeader toggle={toggleModal}>
        Restaurar Contraseña
      </ModalHeader>

      <ModalBody>

        <div className="text-center mb-4">

          <div className="mb-3">
            <svg width="90" height="90" viewBox="0 0 20 20">
              <path
                fill="#DB5227"
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98z"
              />
            </svg>
          </div>

          <h5 className="fw-bold">
            Cambiar contraseña
          </h5>

          <p className="text-muted">
            Actualiza tu contraseña para mantener la seguridad de tu cuenta.
          </p>

        </div>

        <div className="pro-form-card">

          <label className="pro-label">Contraseña Actual</label>
          <input
            type="password"
            className="pro-input"
            value={formData.passwordActual || ""}
            onChange={(e) =>
              handleModalInputChange("passwordActual", e.target.value)
            }
          />

          <label className="pro-label mt-3">Nueva Contraseña</label>
          <input
            type="password"
            className="pro-input"
            value={formData.password || ""}
            onChange={(e) =>
              handleModalInputChange("password", e.target.value)
            }
          />

          <label className="pro-label mt-3">Confirmar Contraseña</label>
          <input
            type="password"
            className="pro-input"
            value={formData.confirmPassword}
            onChange={(e) =>
              handleModalInputChange("confirmPassword", e.target.value)
            }
          />

        </div>

      </ModalBody>

      <ModalFooter>

        <Button className="pro-btn-primary" onClick={handleResetPassword}>
          Restaurar
        </Button>

        <Button className="pro-btn-cancel" onClick={toggleModal}>
          Cancelar
        </Button>

      </ModalFooter>

    </Modal>

    {/* ================= FORM ================= */}
    <div className="pro-card mt-4">

      <div className="p-4">

        <Form onSubmit={isEditing ? editarProfile : guardarProfile}>

          <div className="row g-3">

            {/* USER */}
            <div className="col-md-12">
              <label className="pro-label">Usuario</label>
              <input
                type="text"
                className="pro-input"
                value={formData.username || ""}
                disabled
              />
            </div>

            {/* NOMBRE */}
            <div className="col-md-6">
              <label className="pro-label">Nombre Completo</label>
              <input
                type="text"
                className="pro-input"
                value={formData.docNom}
                onChange={(e) =>
                  handleInputChange("docNom", e.target.value)
                }
              />
            </div>

            {/* CEDULA */}
            <div className="col-md-6">
              <label className="pro-label">Cédula</label>
              <input
                type="text"
                className="pro-input"
                value={formData.docCed}
                onChange={(e) =>
                  handleInputChange("docCed", e.target.value)
                }
              />
            </div>

            {/* CENTRO */}
            <div className="col-md-6">
              <label className="pro-label">Centro de Salud</label>
              <input
                type="text"
                className="pro-input"
                value={formData.nomCentro}
                onChange={(e) =>
                  handleInputChange("nomCentro", e.target.value)
                }
              />
            </div>

            {/* ESPECIALIDAD */}
            <div className="col-md-6">
              <label className="pro-label">Especialidad</label>
              <input
                type="text"
                className="pro-input"
                value={formData.docEsp}
                onChange={(e) =>
                  handleInputChange("docEsp", e.target.value)
                }
              />
            </div>

            {/* PAIS */}
            <div className="col-md-6">
              <label className="pro-label">País</label>
              <select
                className="pro-input"
                value={formData.docNac}
                onChange={(e) =>
                  handleInputChange("docNac", e.target.value)
                }
              >
                {countries.map((c) => (
                  <option key={c.code} value={c.pais}>
                    {c.pais}
                  </option>
                ))}
              </select>
            </div>

            {/* PROVINCIA */}
            <div className="col-md-6">
              <label className="pro-label">Provincia</label>
              <select
                className="pro-input"
                value={formData.docProv}
                onChange={(e) =>
                  handleInputChange("docProv", e.target.value)
                }
              >
                {provincias.map((p) => (
                  <option key={p.idProvincia} value={p.Description}>
                    {p.Description}
                  </option>
                ))}
              </select>
            </div>

            {/* DIRECCION */}
            <div className="col-md-12">
              <label className="pro-label">Dirección</label>
              <input
                type="text"
                className="pro-input"
                value={formData.docDir}
                onChange={(e) =>
                  handleInputChange("docDir", e.target.value)
                }
              />
            </div>

            {/* CONTACTO */}
            <div className="col-md-6">
              <label className="pro-label">Email</label>
              <input
                type="text"
                className="pro-input"
                value={formData.docEmail}
                onChange={(e) =>
                  handleInputChange("docEmail", e.target.value)
                }
              />
            </div>

            <div className="col-md-6">
              <label className="pro-label">Teléfono</label>
              <input
                type="text"
                className="pro-input"
                value={formData.docTel}
                onChange={(e) =>
                  handleInputChange("docTel", e.target.value)
                }
              />
            </div>

          </div>

          {/* ================= IMAGES ================= */}
          <div className="mt-5">

            <div className="pro-section-mini-header">
              <h5>Imágenes</h5>
            </div>

            <div className="row g-4 mt-2">

              <div className="col-md-6 text-center">
                <label className="pro-label">Logo 1</label>
                <div className="pro-card p-4 text-center"></div>
              </div>

              <div className="col-md-6 text-center">
                <label className="pro-label">Logo 2</label>
                <div className="pro-card p-4 text-center"></div>
              </div>

            </div>

            <p className="text-danger mt-3 fst-italic">
              Si desea cambiar los logos, contacte a <b>SoftGeek</b>.
            </p>

          </div>

          {/* ================= ACTIONS ================= */}
          <div className="d-flex justify-content-end gap-3 mt-4 flex-wrap">

            <button
              type="button"
              className="pro-btn-cancel"
              onClick={handleCancel}
            >
              Cancelar
            </button>

            <button
              type="submit"
              className="pro-btn-primary"
            >
              {isEditing ? "Guardar Edición" : "Guardar"}
            </button>

          </div>

        </Form>

      </div>

    </div>

  </div>

);
};

export default ProfileForm;
